import { useCallback, useEffect, useRef, useState } from 'react';
import { BytebeatEngine } from '@/lib/bytebeatEngine';

interface UseBytebeatEngineOptions {
  code: string;
  bitrate: number;
  args: Record<string, number>;
}

interface UseBytebeatEngineReturn {
  isPlaying: boolean;
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
  volume: number;
  error: string | null;
}

export function useBytebeatEngine(options: UseBytebeatEngineOptions): UseBytebeatEngineReturn {
  const engineRef = useRef<BytebeatEngine | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [error, setError] = useState<string | null>(null);

  // Initialize / re-initialize engine when code or bitrate changes
  useEffect(() => {
    // Stop and destroy old engine
    if (engineRef.current) {
      engineRef.current.destroy();
      engineRef.current = null;
      setIsPlaying(false);
    }

    setError(null);

    if (!options.code.trim()) return;

    const engine = new BytebeatEngine({
      code: options.code,
      bitrate: options.bitrate,
      args: options.args,
      onError: (err) => {
        setError(err);
        setIsPlaying(false);
      },
    });

    engine.setVolume(volume);
    engineRef.current = engine;

    return () => {
      engine.destroy();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.code, options.bitrate]);

  // Update args when they change without rebuilding engine
  useEffect(() => {
    engineRef.current?.updateArgs(options.args);
  }, [options.args]);

  const play = useCallback(async () => {
    if (!engineRef.current) return;
    try {
      await engineRef.current.play();
      setIsPlaying(true);
      setError(null);
    } catch (e) {
      setError(String(e));
      setIsPlaying(false);
    }
  }, []);

  const pause = useCallback(() => {
    engineRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const stop = useCallback(() => {
    engineRef.current?.stop();
    setIsPlaying(false);
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    engineRef.current?.setVolume(v);
  }, []);

  return { isPlaying, play, pause, stop, setVolume, volume, error };
}
